import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  Linking, StyleSheet, Switch
} from "react-native";
import { Style, styles } from "../../theme/styles";
import { Button, Gap } from "../../components";
import { ButtonType, Frame, IsMobile } from "../../theme";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { CircumstancesType } from "../../constants";
import { Analytics, Auth } from "aws-amplify";
import {
  AuthStackParams
} from "../../navigation/Auth/AuthStackParams";
import {ForgotPasswordRoute, LoginRoute, SignupRoute} from "../../navigation/Auth/routes";
import Dropdown from "../../components/Dropdown";
import TextInput from "../../components/TextInput"; // Your custom TextInput component
import Checkbox, { CheckboxPressEvent } from "../../components/Checkbox";
import {getErrorMessage} from "../../utils/errorFormHandlers";
import {AppTheme, useTheme} from "../../theme/colors";
import {NavigationProp, RouteProp, useNavigation, useRoute} from "@react-navigation/native";
import * as Keychain from 'react-native-keychain'; // Import Keychain

type ErrorType = {
  type:
    | "given_name"
    | "family_name"
    | "email"
    | "password"
    | "confirm_password"
    | "gender"
    | "profile_type"
    | "global"
    | "circumstances";
  message: string;
};

const Signup: FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [savePassword, setSavePassword] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [given_name, setGivenName] = useState<string>();
  const [family_name, setFamilyName] = useState<string>();
  const [gender, setGender] = useState<string>();
  const [circumstances, setCircumstances] = useState<CircumstancesType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorType>();
  const [showingOTPModal, setShowingOTPModal] = useState(false);
  const [resendAvailable, setResendAvailable] = useState(false);
  const [count, setCount] = useState(60);
  const navigation = useNavigation<NavigationProp<AuthStackParams>>();
  const [code, setCode] = useState<string>("");
  const [isPasswordVisible, setPasswordVisibility] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisibility] = useState(false);
  const route = useRoute<RouteProp<AuthStackParams, "SignupRoute">>();
  const [email, setEmail] = useState<string>();

  const theme = useTheme();
  const errorStyles = getErrorStyle(theme);

  const goBack = () => {
    setShowingOTPModal(false);
  };


  useEffect(() => {
    if (route.params?.verify) {
      handleResendCode();
      setShowingOTPModal(true);
    }
    if (route.params?.email) {
      setEmail(route.params?.email);
    }

  }, [route.params]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (count > 0) setCount((prev) => prev - 1);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [count]);

  useEffect(() => {
    if (count === 0) setResendAvailable(true);
  }, [count]);

  const handleConfirmCode = useCallback(async () => {
    if (!email) return;
    try {
      const res = await Auth.confirmSignUp(email, code);
      setShowingOTPModal(false);

      Analytics.record({
        name: "UserRegister",
        attributes: {
          email: email,
        },
      });

      if (Platform.OS === "ios" && savePassword) {
        await Keychain.setGenericPassword(email, password); // Use email as username
      }

      navigation.navigate(LoginRoute);
    } catch (error: any) {
      Alert.alert(error.message ?? "");
    }
  }, [email, code]);

  const handleResendCode = useCallback(async () => {
    try {
      setResendAvailable(false);
      setCount(60);

      const __email = route.params.email ?? email;

      const res = await Auth.resendSignUp(__email as string);
      Alert.alert("Code was sent successfully!");
    } catch (error) {
      // do nothing
    }
  }, [email]);

  const genderOptions = useMemo(() => {
    return [
      "Female/woman",
      "Male/man",
      "Non-binary",
      "Transgender",
      "Intersex",
      "Other",
    ];
  }, []);

  useEffect(() => {
    setError(undefined);
  }, [email, password, given_name, family_name, gender, confirmPassword]);

  const handleSignup = async () => {
    setError(undefined);
    const emailRegex = /^[\w\.-\\+]+@[a-zA-Z\d\.-]+\.[a-zA-Z]{2,}$/;

    const emailValid = emailRegex.test(email as string);
    const passwordsMatch = password === confirmPassword;

    if (!given_name) {
      setError({
        type: "given_name",
        message: "First Name was not provided",
      });
      return;
    }

    if (!family_name) {
      setError({
        type: "family_name",
        message: "Last Name was not provided",
      });
      return;
    }

    if (!emailValid) {
      setError({ type: "email", message: "Email is not valid" });
      return;
    }

    if (!passwordsMatch) {
      setError({
        type: "confirm_password",
        message: "Passwords do not match",
      });
      return;
    }

    if (!gender) {
      setError({
        type: "gender",
        message: "Gender was not selected",
      });
      return;
    }

    if (!circumstances.length) {
      setError({
        type: "circumstances",
        message: "Mark at least one circumstance",
      });
      return;
    }

    if (email && password)
      try {
        setLoading(true);
        const { user } = await Auth.signUp({
          username: email,
          password,
          attributes: {
            gender,
            given_name,
            family_name,
            "custom:circumstances": circumstances.join(","),
            email,
          },
        });

        setShowingOTPModal(true);
      } catch (error: any) {
        console.log("error:" + error.message);
        if (error?.code === "UsernameExistsException") {
          navigation.navigate(ForgotPasswordRoute, {
            email,
            message: true,
          });
        } else {
          setError({
            type: "global",
            message: error?.message,
          });
        }
      } finally {
        setLoading(false);
      }
  };


  const handleTermsOfServicePress = () => {
    Linking.openURL("https://theonespace.co.uk/privacy-notice");
  };

  const onPressCircumstancesCheckboxes = (event: CheckboxPressEvent) => {
    const { name, checked } = event;
    if (checked) {
      setCircumstances([...circumstances, name as CircumstancesType]);
    } else {
      setCircumstances((prevState) => {
        return prevState.filter((n) => n !== name);
      });
    }
  };

  const renderCircumstancesCheckboxes = () => (
    <View>
      <Text style={{ fontSize: 18, fontWeight: "500" }}>Circumstances:</Text>
      <Checkbox
        name={CircumstancesType.NEURODIVERSITY}
        text={CircumstancesType.NEURODIVERSITY}
        checked={circumstances.includes(CircumstancesType.NEURODIVERSITY)}
        onPressCheckbox={onPressCircumstancesCheckboxes}
      />
      <Checkbox
        name={CircumstancesType.MENTAL_HEALTH}
        text={CircumstancesType.MENTAL_HEALTH}
        checked={circumstances.includes(CircumstancesType.MENTAL_HEALTH)}
        onPressCheckbox={onPressCircumstancesCheckboxes}
      />
      <Checkbox
        name={CircumstancesType.WELLBEING}
        text={CircumstancesType.WELLBEING}
        checked={circumstances.includes(CircumstancesType.WELLBEING)}
        onPressCheckbox={onPressCircumstancesCheckboxes}
      />
      <Checkbox
        name={CircumstancesType.DISABILITY}
        text={CircumstancesType.DISABILITY}
        checked={circumstances.includes(CircumstancesType.DISABILITY)}
        onPressCheckbox={onPressCircumstancesCheckboxes}
      />
      {circumstances.length === 0 && (
        <Text style={{ color: "red", fontSize: 14, marginTop: 5 }}>
          Please select at least one circumstance.
        </Text>
      )}
    </View>
  );

  return loading ? (
    <View
      style={[
        styles.flex1,
        styles.center,
        { backgroundColor: theme.BACKGROUND },
      ]}
    >
      <ActivityIndicator color={theme.ACCENT} size="large" />
    </View>
  ) : (
    <KeyboardAvoidingView
      style={styles.flex1}
      behavior={Platform.select({ ios: "padding", android: "height" })}
    >
      <ScrollView style={{ ...$container, backgroundColor: theme.BACKGROUND }}>
        <Text style={[styles.heading, styles.textCenter, { fontSize: 30 }]}>
          Let’s get started
        </Text>
        <View
          style={[IsMobile ? styles.moileContainer : styles.desktopContainer]}
        >
          <Gap gap={20} />
          <TextInput
            onChangeText={(text) => setGivenName(text)}
            placeholder="Please enter your first name"
            value={given_name}
            label={error?.type === "given_name" ? error.message : "First Name"}
            error={error?.type === "given_name"}
            style={[IsMobile ? styles.mobileinputs : styles.desktopinput]}
            autoComplete="given-name"
            autoCorrect={false}
          />
          <Gap gap={10} />
          <TextInput
            onChangeText={(text) => setFamilyName(text)}
            placeholder="Please enter your last name"
            label={error?.type === "family_name" ? error.message : "Last Name"}
            error={error?.type === "family_name"}
            autoComplete="family-name"
            value={family_name}
            style={[IsMobile ? styles.mobileinputs : styles.desktopinput]}
            autoCorrect={false}
          />
          <Gap gap={10} />
          <TextInput
            keyboardType="email-address"
            autoCorrect={false}
            autoCapitalize="none"
            placeholder="Please enter your email address"
            textContentType="emailAddress"
            autoComplete="email"
            value={email}
            error={error?.type === "email"}
            label={error?.type === "email" ? error.message : "Email"}
            onChangeText={(text) => setEmail(text)}
            style={[IsMobile ? styles.mobileinputs : styles.desktopinput]}
          />
          <Gap gap={20} />
          <View style={{ marginLeft: 10, gap: 20 }}>
            {IsMobile ? (
              <>
                <Dropdown
                  data={genderOptions}
                  onSelect={(value) => setGender(value)}
                  label={error?.type === "gender" ? error.message : "Gender"}
                  error={error?.type === "gender"}
                  simple
                  renderDropdownIcon={() => (
                    <Ionicons
                      name="chevron-down"
                      size={20}
                      color={theme.LIGHT_GREY}
                    />
                  )}
                  buttonTextStyle={{
                    textAlign: "left",
                    color: theme.LIGHT_GREY,
                  }}
                />
              </>
            ) : (
              <>
                <Dropdown
                  data={genderOptions}
                  onSelect={(value) => setGender(value)}
                  label={error?.type === "gender" ? error.message : "Gender"}
                  simple
                  error={error?.type === "gender"}
                  renderDropdownIcon={() => (
                    <Ionicons
                      name="chevron-down"
                      size={20}
                      color={theme.LIGHT_GREY}
                    />
                  )}
                  buttonTextStyle={{
                    textAlign: "left",
                    color: theme.LIGHT_GREY,
                  }}
                />
              </>
            )}
          </View>
          <Gap gap={20} />
          {renderCircumstancesCheckboxes()}
          <Gap gap={20} />
          <View style={{ position: "relative" }}>
            <TextInput
              autoCorrect={false}
              autoCapitalize="none"
              value={password}
              placeholder="Password"
              textContentType="newPassword" // Key prop for strong password on iOS
              autoComplete="new-password"  // Key prop for cross-platform autofill
              secureTextEntry={!isPasswordVisible}
              error={error?.type === "password" || error?.type === "confirm_password"}
              label={
                error?.type === "password" || error?.type === "confirm_password"
                  ? error.message
                  : "Password"
              }
              blurOnSubmit={true}
              onSubmitEditing={() => Keyboard.dismiss()}
              onChangeText={(text) => setPassword(text)}
              style={[IsMobile ? styles.mobileinputs : styles.desktopinput]}
            />
            <TouchableOpacity
              onPress={() => setPasswordVisibility(!isPasswordVisible)}
              style={{ position: "absolute", right: 10, top: 19 }}
            >
              <Ionicons
                name={isPasswordVisible ? "eye-off" : "eye"}
                size={20}
              />
            </TouchableOpacity>
          </View>
          <Gap gap={20} />

          <View style={{ position: "relative" }}>
            <TextInput
              autoCorrect={false}
              autoCapitalize="none"
              placeholder="Confirm Password"
              textContentType="newPassword" // Important: Use newPassword here too
              autoComplete="new-password"
              value={confirmPassword}
              label={
                error?.type === "password" || error?.type === "confirm_password"
                  ? error.message
                  : "Confirm Password"
              }
              error={error?.type === "confirm_password"}
              secureTextEntry={!isConfirmPasswordVisible}
              onChangeText={(text) => setConfirmPassword(text)}
              style={[IsMobile ? styles.mobileinputs : styles.desktopinput]}
            />
            <TouchableOpacity
              onPress={() => setConfirmPasswordVisibility(!isConfirmPasswordVisible)}
              style={{ position: "absolute", right: 10, top: 19 }}
            >
              <Ionicons
                name={isConfirmPasswordVisible ? "eye-off" : "eye"}
                size={20}
              />
            </TouchableOpacity>
          </View>
          <Gap gap={20} />
          {Platform.OS === "ios" && (
            <View style={$savePasswordContainer}>
              <Switch
                trackColor={{ false: "#767577", true: theme.ACCENT }}
                thumbColor={savePassword ? "#f4f3f4" : "#f4f3f4"}
                ios_backgroundColor="#3e3e3e"
                onValueChange={() => setSavePassword(previousState => !previousState)}
                value={savePassword}
              />
              <Text style={{ color: theme.TEXT, marginLeft: 10 }}>Save Password</Text>
            </View>
          )}
          <View
            style={[
              !IsMobile && {
                width: 350,
              },
            ]}
          >
            <Text
              style={{
                color: theme.LIGHT_GREY,
                fontSize: 13,
                textAlign: "justify"
              }}
            >
              By creating an account, you agree to the{" "}
              <TouchableOpacity onPress={handleTermsOfServicePress}>
                <Text style={{ color: theme.PRIMARY, textDecorationLine: "underline" }}>
                  Terms of Service
                </Text>
              </TouchableOpacity>
              . For more information about OneSpace's privacy practices, see the{" "}
              <TouchableOpacity onPress={handleTermsOfServicePress}>
                <Text style={{ color: theme.PRIMARY, textDecorationLine: "underline" }}>
                  OneSpace Privacy Statement
                </Text>
              </TouchableOpacity>
              . We'll occasionally send you account-related emails.
            </Text>
          </View>
          <Gap gap={!IsMobile ? 60 : 20} />
          {error && error.type === "global" && (
            <Text style={errorStyles.errorTextStyle}>
              {error.message}
            </Text>
          )}


          <View style={styles.center}>
            <Button
              onPress={handleSignup}
              title="Sign Up"
              variant={ButtonType.secondary}
            />
          </View>
          <View
            style={[
              !IsMobile && {
                width: Frame.width / 3.8,
              },
            ]}
          >
            <TouchableOpacity
              onPress={() => {
                navigation.navigate(LoginRoute);
              }}
              style={$createAccount}
            >
              <Text
                style={{
                  fontWeight: "600",
                  color: theme.ACCENT,
                  fontSize: 15,

                  textDecorationLine: "underline",
                }}
              >
                If you already have an account, let's log in
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
      <Modal visible={showingOTPModal} presentationStyle="formSheet">
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <View
            style={[
              styles.flex1,
              { padding: 20 },
              !IsMobile && { width: Frame.width / 3.5 },
            ]}
          >
            <View style={[styles.flex1, { justifyContent: "center" }]}>
              <Text style={{ fontSize: 30, fontWeight: "bold" }}>
                Verify your Email
              </Text>
              <Text>The confirmation code was sent to {email}</Text>
            </View>
            <View
              style={{
                flex: 4,
                justifyContent: "flex-start",
              }}
            >
              <TextInput
                autoCorrect={false}
                autoFocus
                autoCapitalize="none"
                placeholder="Enter the code received by email"
                label="One Time Passcode"
                maxLength={6}
                keyboardType="number-pad"
                textContentType="oneTimeCode"
                value={code}
                onChangeText={(text) => setCode(text)}
                style={{
                  width: IsMobile ? Frame.width / 1.05 : undefined,
                  backgroundColor: theme.BACKGROUND,
                }}
              />

              <Gap gap={25} />

              <Text>Didn't receive the Code? </Text>
              {resendAvailable ? (
                <TouchableOpacity onPress={handleResendCode}>
                  <Text
                    style={{
                      color: theme.PRIMARY,
                      fontWeight: "bold",
                      lineHeight: 30,
                    }}
                  >
                    Resend Code
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text style={{ lineHeight: 30 }}>
                  Request code in {count} seconds
                </Text>
              )}

              <Gap gap={50} />
              <View style={{ alignItems: "center", justifyContent: "center" }}>
                <Button
                  title="Verify Account"
                  variant={ButtonType.secondary}
                  onPress={handleConfirmCode}
                />
                <Gap gap={20} />
                <Button
                  title="Go Back"
                  variant={ButtonType.primary}
                  onPress={goBack}
                />
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
};

export default Signup;

const $container: Style = {
  flex: 1,
  padding: 20,
};
const $createAccount: Style = {
  alignSelf: "center",
  marginTop: 20,
  marginBottom: 40
};
const $savePasswordContainer: Style = {
  flexDirection: "row",
  alignItems: "center",
  marginBottom: 10,
  justifyContent: "flex-end", // Align to the right
};
const getErrorStyle = (theme: AppTheme) =>
  StyleSheet.create({
    errorTextStyle: {
      color: theme.ERROR,
      fontWeight: "bold",
      backgroundColor: theme.BACKGROUND,
      padding: 10,
      textAlign: "center",
      marginBottom: 20
    },
    labelTextStyle: {
      // any styles applied on label text comp
      backgroundColor: theme.BACKGROUND,
    }

  });
